/**
 * AccountMorph — the avatar-to-X card-grow overlay.
 *
 * When a rider taps the avatar on any surface that hosts an
 * <AccountAvatarButton />, the SAME node morphs into a full-screen
 * account sheet (avatar circle → X) via framer-motion `layoutId`.
 * Booking state (ScheduleSetup + BookingSheet) stays mounted the
 * entire time; the overlay is a UI layer above it. Tapping X shrinks
 * back to the exact avatar location the rider left.
 *
 * There is NO /account route push. The old pill drawer
 * (RiderAccountSurface) is not used here.
 *
 * Public API:
 *   <AccountMorphProvider>            wrap the app once
 *     <AccountMorphOverlay />          render once at the top level
 *     <AccountAvatarButton />          drop on any surface header
 *
 * Contract: exactly one open state; opening from any avatar node
 * lifts the shared node ("rider-account-node") to fill the viewport.
 */
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, User } from "lucide-react";

import Account from "@/apps/rider/pages/Account";

const AccountMorphContext = createContext({ open: false, setOpen: () => {} });

export function AccountMorphProvider({ children }) {
  const [open, setOpen] = useState(false);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return (
    <AccountMorphContext.Provider value={value}>
      {children}
    </AccountMorphContext.Provider>
  );
}

export function useAccountMorph() {
  return useContext(AccountMorphContext);
}

/**
 * Avatar circle button. Drop this on ANY surface header that should
 * open the account overlay. Uses `layoutId` so framer-motion animates
 * the same node into the overlay's X close button.
 */
export function AccountAvatarButton({ testId = "account-avatar-button", size = 40 }) {
  const { open, setOpen } = useAccountMorph();
  // When open, the shared node lives in the overlay; render nothing
  // here so framer-motion has a single source/target pair.
  if (open) return <div style={{ width: size, height: size }} aria-hidden="true" />;
  return (
    <motion.button
      type="button"
      layoutId="rider-account-node"
      onClick={() => setOpen(true)}
      className="inline-flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: "rgba(255,255,255,0.08)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
        color: "#F9F7F4",
      }}
      transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
      aria-label="Open account"
      data-testid={testId}
    >
      <User size={Math.round(size * 0.45)} strokeWidth={2} />
    </motion.button>
  );
}

/**
 * The overlay. Renders full-screen when open; nothing when closed.
 * Uses `layoutId="rider-account-node"` on the X so the shared node
 * animates smoothly. Uses `layoutId="rider-account-card"` on the
 * container so the surrounding card grows from where the avatar sat.
 */
export function AccountMorphOverlay() {
  const { open, setOpen } = useAccountMorph();
  const reduceMotion = useReducedMotion();

  // ESC key closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="account-morph-overlay"
          className="fixed inset-0"
          style={{
            zIndex: 200,
            background: "rgba(7,13,31,0.55)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.28 }}
          data-testid="account-morph-backdrop"
          onClick={() => setOpen(false)}
        >
          <motion.div
            className="fixed inset-0 flex flex-col"
            style={{
              background: "#0A1128",
              color: "#F9F7F4",
            }}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: reduceMotion ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
            data-testid="account-morph-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Account"
          >
            {/* Header: X in the exact spot the avatar sat */}
            <div
              className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0"
              style={{
                paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
              }}
            >
              <div style={{ height: 26 }}>
                <img src="/assets/link-full-logo.svg" alt="Link" style={{ height: 26 }} />
              </div>
              <motion.button
                type="button"
                layoutId="rider-account-node"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center rounded-full"
                style={{
                  width: 40,
                  height: 40,
                  background: "rgba(255,255,255,0.08)",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
                  color: "#F9F7F4",
                }}
                transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
                aria-label="Close account"
                data-testid="account-morph-close"
              >
                <X size={18} strokeWidth={2} />
              </motion.button>
            </div>

            {/* Account rows — reuse existing Account page in sheet mode. */}
            <div
              className="flex-1 min-h-0 overflow-y-auto"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 32px)" }}
              data-testid="account-morph-scroll"
            >
              <Account inSheet hideIdentityHeader />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
